import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { AnswerDto, AnswerService } from './answer.service';
import { EventService } from './event.service';
import {
  ArtifactAssetDto,
  EventTimerSettingDto,
  GamePreferencesService
} from './game-preferences.service';
import {
  PortableEventConfig,
  PortableGameConfig,
  PortableGameConfigSchemaVersion,
  PortableMediaConfig,
  PortableMediaReference,
  PortableRoundConfig,
  PortableTeamConfig,
  PortableThemeConfig,
  PortableThemeVisualBlockKey
} from './portable-config.models';
import { PortableConfigValidationResult, PortableConfigValidatorService } from './portable-config-validator.service';
import { QuestionAssignmentDto, QuestionAssignmentService } from './question-assignment.service';
import { QuestionDto, QuestionService } from './question.service';
import { TeamDto, TeamPlayerDto, TeamService } from './team.service';
import {
  EventThemeAssignmentDto,
  GameThemeCatalogService,
  GameThemeDto
} from './game-theme-catalog.service';

export interface BuildPortableConfigOverrides {
  gamePlayRulePreference?: 1 | 2;
  maxWrongAttempts?: number;
  theme?: PortableThemeConfig;
}

export interface BuildPortableConfigResult {
  config: PortableGameConfig;
  validation: PortableConfigValidationResult;
}

export interface ServerPortableExportResult {
  fileName: string;
  blob: Blob;
}

export function serializePortableGameConfig(config: PortableGameConfig): string {
  return JSON.stringify(config, null, 2);
}

@Injectable({
  providedIn: 'root'
})
export class PortableConfigExportService {
  private readonly http = inject(HttpClient);
  private readonly eventService = inject(EventService);
  private readonly teamService = inject(TeamService);
  private readonly questionService = inject(QuestionService);
  private readonly answerService = inject(AnswerService);
  private readonly questionAssignmentService = inject(QuestionAssignmentService);
  private readonly gamePreferencesService = inject(GamePreferencesService);
  private readonly gameThemeCatalogService = inject(GameThemeCatalogService);
  private readonly validator = inject(PortableConfigValidatorService);

  buildForEvent(eventId: number, overrides?: BuildPortableConfigOverrides): Observable<BuildPortableConfigResult> {
    return forkJoin({
      event: this.eventService.getById(eventId),
      eventTeamAssignments: this.teamService.getEventTeamsByEventId(eventId),
      allQuestionAssignments: this.questionAssignmentService.getAll()
    }).pipe(
      switchMap(({ event, eventTeamAssignments, allQuestionAssignments }) => {
        const eventQuestions = allQuestionAssignments
          .filter(assignment => assignment.eventId === eventId)
          .sort((left, right) => left.round - right.round);

        return forkJoin({
          teams: this.loadTeams(eventTeamAssignments.map(assignment => assignment.teamId)),
          rounds: this.loadRounds(eventQuestions),
          theme: this.loadThemeConfig(event.id, event.organizationId),
          timerSettings: this.gamePreferencesService.getTimerSettingsByEvent(event.id)
        }).pipe(
          map(({ teams, rounds, theme, timerSettings }) => this.buildResult(event, teams, rounds, theme, timerSettings, overrides))
        );
      })
    );
  }

  exportFromServer(eventId: number): Observable<ServerPortableExportResult> {
    return this.http
      .get(`/api/eventconfigurations/event/${eventId}/export-package`, {
        observe: 'response',
        responseType: 'blob'
      })
      .pipe(
        map((response: HttpResponse<Blob>) => ({
          fileName: this.getDownloadFileName(response) ?? this.buildFallbackPackageFileName(eventId),
          blob: response.body ?? new Blob([], { type: 'application/zip' })
        }))
      );
  }

  private loadThemeConfig(eventId: number, organizationId: number): Observable<PortableThemeConfig> {
    return forkJoin({
      assignment: this.gameThemeCatalogService.getAssignmentByEvent(eventId),
      themes: this.gameThemeCatalogService.getAll(),
      artifacts: this.gamePreferencesService.getArtifactsByOrganization(organizationId)
    }).pipe(
      map(({ assignment, themes, artifacts }) => this.mapThemeConfig(assignment, themes, artifacts, organizationId))
    );
  }

  createFileName(config: PortableGameConfig): string {
    const organizationSegment = this.normalizeNameSegment('', `org-${config.event.organizationId}`);
    const eventSegment = this.normalizeNameSegment(config.event.name, `event-${config.event.id}`);
    const timestampSegment = this.toSafeUtcTimestamp(config.metadata.exportedAtUtc);
    return `${organizationSegment}-${eventSegment}-export-${timestampSegment}.${PortableGameConfigSchemaVersion}.json`;
  }

  toJson(config: PortableGameConfig): string {
    return serializePortableGameConfig(config);
  }

  private getDownloadFileName(response: HttpResponse<Blob>): string | null {
    const contentDisposition = response.headers.get('content-disposition');
    if (!contentDisposition) {
      return null;
    }

    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
      return decodeURIComponent(utfMatch[1]);
    }

    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1] ?? null;
  }

  private buildFallbackPackageFileName(eventId: number): string {
    const timestampSegment = this.toSafeUtcTimestamp(new Date().toISOString());
    return `org-unknown-event-${eventId}-export-${timestampSegment}.zip`;
  }

  private normalizeNameSegment(value: string, fallback: string): string {
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/(^-|-$)/g, '');

    return normalized || fallback;
  }

  private toSafeUtcTimestamp(value: string): string {
    const date = new Date(value);
    const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
    return safeDate.toISOString().replace(/:/g, '-');
  }

  private loadTeams(teamIds: number[]): Observable<Array<{ team: TeamDto; players: TeamPlayerDto[] }>> {
    if (teamIds.length === 0) {
      return of([] as Array<{ team: TeamDto; players: TeamPlayerDto[] }>);
    }

    return forkJoin(
      teamIds.slice(0, 2).map(teamId =>
        forkJoin({
          team: this.teamService.getById(teamId),
          players: this.teamService.getPlayersByTeamId(teamId)
        })
      )
    );
  }

  private loadRounds(assignments: QuestionAssignmentDto[]): Observable<PortableRoundConfig[]> {
    if (assignments.length === 0) {
      return of([] as PortableRoundConfig[]);
    }

    return forkJoin(assignments.map(assignment => this.loadRound(assignment)));
  }

  private loadRound(assignment: QuestionAssignmentDto): Observable<PortableRoundConfig> {
    return forkJoin({
      question: this.questionService.getAll().pipe(
        map((questions: QuestionDto[]) => questions.find(item => item.id === assignment.questionId) ?? null)
      ),
      answers: this.answerService.getByQuestionId(assignment.questionId)
    }).pipe(
      map(({ question, answers }) => ({
        roundNumber: assignment.round,
        question: {
          id: assignment.questionId,
          text: question?.questionText ?? '',
          answers: this.mapAnswers(answers)
        }
      }))
    );
  }

  private mapAnswers(answers: AnswerDto[]) {
    return [...answers]
      .sort((left, right) => left.order - right.order)
      .filter(answer => !!answer.answerText.trim())
      .map(answer => ({
        orderNumber: answer.order,
        text: answer.answerText,
        points: answer.points
      }));
  }

  private buildResult(
    event: {
      id: number;
      organizationId: number;
      name: string;
      description?: string;
      scheduledDateTime: string;
      numberOfRounds: number;
    },
    teams: Array<{ team: TeamDto; players: TeamPlayerDto[] }>,
    rounds: PortableRoundConfig[],
    theme: PortableThemeConfig,
    timerSettings: EventTimerSettingDto | null,
    overrides?: BuildPortableConfigOverrides
  ): BuildPortableConfigResult {
    const config: PortableGameConfig = {
      schemaVersion: PortableGameConfigSchemaVersion,
      metadata: {
        exportedAtUtc: new Date().toISOString(),
        source: 'Portal'
      },
      event: this.mapEvent(event),
      teams: this.mapTeams(teams),
      rounds,
      gamePreferences: {
        gamePlayRulePreference: overrides?.gamePlayRulePreference ?? 1,
        maxWrongAttempts: overrides?.maxWrongAttempts ?? 3,
        WinnerTimerValue: 5,
        timers: {
          podium: {
            enabled: timerSettings?.podiumTimerEnabled ?? true,
            seconds: timerSettings?.podiumTimerSeconds ?? 4
          },
          answer: {
            enabled: timerSettings?.answerTimerEnabled ?? true,
            seconds: timerSettings?.answerTimerSeconds ?? 15
          }
        }
      },
      theme: overrides?.theme ?? theme
    };

    const validation = this.validator.validate(config);
    return { config, validation };
  }

  private mapEvent(event: {
    id: number;
    organizationId: number;
    name: string;
    description?: string;
    scheduledDateTime: string;
    numberOfRounds: number;
  }): PortableEventConfig {
    return {
      id: event.id,
      organizationId: event.organizationId,
      name: event.name,
      description: event.description,
      scheduledDateTime: event.scheduledDateTime,
      numberOfRounds: event.numberOfRounds
    };
  }

  private mapTeams(teams: Array<{ team: TeamDto; players: TeamPlayerDto[] }>): [PortableTeamConfig, PortableTeamConfig] {
    const defaults: [PortableTeamConfig, PortableTeamConfig] = [
      {
        teamNumber: 1,
        name: 'Team 1',
        members: []
      },
      {
        teamNumber: 2,
        name: 'Team 2',
        members: []
      }
    ];

    for (let index = 0; index < Math.min(2, teams.length); index += 1) {
      const teamNumber = (index + 1) as 1 | 2;
      const source = teams[index];
      defaults[index] = {
        teamNumber,
        id: source.team.id,
        name: source.team.name,
        members: [...source.players]
          .sort((left, right) => left.order - right.order)
          .map(player => {
            const firstName = player.firstName?.trim() ?? '';
            const lastName = player.lastName?.trim() ?? '';
            const displayName = `${firstName} ${lastName}`.trim() || `Team ${teamNumber} Member ${player.order}`;

            return {
              orderNumber: player.order,
              firstName,
              lastName,
              displayName
            };
          })
      };
    }

    return defaults;
  }

  private mapThemeConfig(
    assignment: EventThemeAssignmentDto | null,
    themes: GameThemeDto[],
    artifacts: ArtifactAssetDto[],
    organizationId: number
  ): PortableThemeConfig {
    const defaultThemeName = 'dark-blue';
    const theme = assignment
      ? themes.find(item => item.id === assignment.gameThemeId && item.isEnabled && !item.isDeleted) ?? null
      : null;

    const artifactsById = new Map<number, ArtifactAssetDto>(artifacts.map(artifact => [artifact.id, artifact]));
    const media: PortableMediaConfig = {
      rightAnswer: [],
      wrongAnswer: [],
      endOfRound: [],
      nextTeamTurn: [],
      nextRound: [],
      intro: [],
      break: [],
      buzzer: [],
      suddenDeath: [],
      endOfGame: [],
      threeThreeAndOut: [],
      threeThreeSuddenDeath: [],
      headToHead: [],
      penaltyShot: []
    };
    const themedByKey = new Map<string, ArtifactAssetDto[]>();

    for (const reference of theme?.artifactReferences ?? []) {
      if (!reference.isEnabled || reference.isDeleted) {
        continue;
      }

      const artifact = artifactsById.get(reference.artifactAssetId);
      if (!artifact || !this.isArtifactEligibleForExport(artifact, organizationId)) {
        continue;
      }

      const section = this.resolveMediaSection(artifact.artifactType);
      const category = this.mapMediaRoleToCategory(reference.mediaRole);
      if (!section || !category) {
        continue;
      }

      const key = `${section}|${this.normalizeCategoryKey(category)}`;
      const existing = themedByKey.get(key) ?? [];
      existing.push(artifact);
      themedByKey.set(key, existing);
    }

    const categories: Array<{ role: string; key: keyof PortableMediaConfig; sections: string[] }> = [
      { role: 'right-answer', key: 'rightAnswer', sections: ['video'] },
      { role: 'wrong-answer', key: 'wrongAnswer', sections: ['video'] },
      { role: 'end-of-round', key: 'endOfRound', sections: ['video'] },
      { role: 'next-team-turn', key: 'nextTeamTurn', sections: ['video'] },
      { role: 'next-round', key: 'nextRound', sections: ['video'] },
      { role: 'intro', key: 'intro', sections: ['video'] },
      { role: 'break', key: 'break', sections: ['video'] },
      { role: 'buzzer', key: 'buzzer', sections: ['sounds'] },
      { role: 'sudden-death', key: 'suddenDeath', sections: ['video'] },
      { role: 'end-of-game', key: 'endOfGame', sections: ['video'] },
      { role: 'three-three-and-out', key: 'threeThreeAndOut', sections: ['video'] },
      { role: 'three-three-sudden-death', key: 'threeThreeSuddenDeath', sections: ['video'] },
      { role: 'head-to-head', key: 'headToHead', sections: ['video'] },
      { role: 'penalty-shot', key: 'penaltyShot', sections: ['video'] },
    ];

    for (const category of categories) {
      let selectedArtifacts: ArtifactAssetDto[] = [];
      for (const section of category.sections) {
        const key = `${section}|${this.normalizeCategoryKey(category.role)}`;
        const themedArtifacts = themedByKey.get(key) ?? [];
        if (themedArtifacts.length > 0) {
          selectedArtifacts = themedArtifacts;
          break;
        }
      }

      if (selectedArtifacts.length === 0) {
        for (const section of category.sections) {
          const fallback = this.resolveFallbackArtifact(artifacts, organizationId, section, category.role);
          if (fallback) {
            selectedArtifacts = [fallback];
            break;
          }
        }
      }

      if (selectedArtifacts.length === 0) {
        continue;
      }

      const references: PortableMediaReference[] = selectedArtifacts.map(artifact => ({
        id: artifact.id,
        fileName: artifact.fileName,
        contentType: artifact.contentType,
        blobPath: artifact.blobPath
      }));

      media[category.key] = references;
    }

    return {
      name: theme?.name ?? defaultThemeName,
      key: theme?.themeKey,
      media,
      visuals: this.mapThemeVisuals(theme?.artifactReferences ?? [], artifactsById, organizationId),
      'theme-variables': { ...(theme?.themeVariables ?? {}) },
      Team1: theme?.displayProperties?.team1 ?? { sections: {} },
      Team2: theme?.displayProperties?.team2 ?? { sections: {} },
      Projector: theme?.displayProperties?.projector ?? { sections: {} },
      EORSummary: theme?.displayProperties?.roundSummary ?? { sections: {} },
      EOGSummary: theme?.displayProperties?.endOfGameSummary ?? { sections: {} }
    };
  }

  private mapThemeVisuals(
    references: GameThemeDto['artifactReferences'],
    artifactsById: Map<number, ArtifactAssetDto>,
    organizationId: number
  ): PortableThemeConfig['visuals'] {
    const visualReference = (role: string): PortableMediaReference | null => {
      const reference = references.find(item => item.isEnabled && !item.isDeleted && item.mediaRole === role && item.sequenceOrder === 1);
      const artifact = reference ? artifactsById.get(reference.artifactAssetId) : null;
      return artifact && this.isArtifactEligibleForExport(artifact, organizationId)
        ? {
            id: artifact.id,
            fileName: artifact.fileName,
            contentType: artifact.contentType,
            blobPath: artifact.blobPath
          }
        : null;
    };
    const hasVisualAssignment = (role: string): boolean =>
      references.some(item => item.isEnabled && !item.isDeleted && item.mediaRole === role);

    const legacyTeamBackground = visualReference('ThemeVisual:background:team');
    const backgroundForTeamScope = (scope: 'team1' | 'team2'): PortableMediaReference | null => {
      const role = `ThemeVisual:background:${scope}`;
      return hasVisualAssignment(role) ? visualReference(role) : legacyTeamBackground;
    };

    const blocks: Record<PortableThemeVisualBlockKey, { name: string; image: PortableMediaReference | null; svg: PortableMediaReference | null }> = {} as Record<PortableThemeVisualBlockKey, { name: string; image: PortableMediaReference | null; svg: PortableMediaReference | null }>;
    const blockNames: Record<PortableThemeVisualBlockKey, string> = {
      team1Left: 'Team 1 Left',
      team1Right: 'Team 1 Right',
      team2Left: 'Team 2 Left',
      team2Right: 'Team 2 Right',
      projectorLeft: 'Projector Left',
      projectorRight: 'Projector Right',
      bodyMain: 'Body Main'
    };

    for (const key of Object.keys(blockNames) as PortableThemeVisualBlockKey[]) {
      blocks[key] = {
        name: blockNames[key],
        image: visualReference(`ThemeVisual:block:${key}:image`),
        svg: visualReference(`ThemeVisual:block:${key}:svg`)
      };
    }

    return {
      backgrounds: {
        team: legacyTeamBackground,
        team1: backgroundForTeamScope('team1'),
        team2: backgroundForTeamScope('team2'),
        projector: visualReference('ThemeVisual:background:projector'),
        roundSummary: visualReference('ThemeVisual:background:roundSummary'),
        endOfGameSummary: visualReference('ThemeVisual:background:endOfGameSummary')
      },
      blocks
    };
  }

  private mapMediaRoleToCategory(mediaRole: string): string | null {
    switch (this.normalizeCategoryKey(mediaRole).replace(/[^a-z0-9]/g, '')) {
      case 'rightanswer':
        return 'right-answer';
      case 'wronganswer':
        return 'wrong-answer';
      case 'endofround':
        return 'end-of-round';
      case 'nextteamturn':
        return 'next-team-turn';
      case 'nextround':
        return 'next-round';
      case 'intro':
        return 'intro';
      case 'break':
        return 'break';
      case 'buzzer':
        return 'buzzer';
      case 'suddendeath':
        return 'sudden-death';
      case 'endofgame':
        return 'end-of-game';
      case 'threethreeandout':
        return 'three-three-and-out';
      case 'threethreesuddendeath':
        return 'three-three-sudden-death';
      case 'headtohead':
        return 'head-to-head';
      case 'penaltyshot':
        return 'penalty-shot';
      default:
        return null;
    }
  }

  private resolveMediaSection(artifactType: string): 'sounds' | 'video' | 'image' | null {
    switch (artifactType.toLowerCase().trim()) {
      case 'sound':
      case 'sounds':
        return 'sounds';
      case 'video':
      case 'videos':
        return 'video';
      case 'image':
      case 'images':
      case 'svg':
        return 'image';
      default:
        return null;
    }
  }

  private isArtifactEligibleForExport(artifact: ArtifactAssetDto, organizationId: number): boolean {
    const inScope = artifact.organizationId === organizationId || artifact.artifactScope.toLowerCase() === 'base';
    const isActive = artifact.isEnabled && artifact.status.toLowerCase() === 'active';
    return inScope && isActive;
  }

  private resolveFallbackArtifact(
    artifacts: ArtifactAssetDto[],
    organizationId: number,
    section: string,
    category: string
  ): ArtifactAssetDto | null {
    const normalizedSection = section.toLowerCase().trim();
    const isTypeMatch = (artifactType: string): boolean => {
      const normalizedType = artifactType.toLowerCase().trim();
      switch (normalizedSection) {
        case 'video':
          return normalizedType === 'video' || normalizedType === 'videos';
        case 'sounds':
          return normalizedType === 'sound' || normalizedType === 'sounds';
        case 'image':
          return normalizedType === 'image' || normalizedType === 'images' || normalizedType === 'svg';
        default:
          return false;
      }
    };

    const normalizedCategory = this.normalizeCategoryKey(category).replace(/[^a-z0-9]/g, '');
    const candidates = artifacts.filter(artifact =>
      this.isArtifactEligibleForExport(artifact, organizationId)
      && artifact.artifactScope.toLowerCase() !== 'user'
      && isTypeMatch(artifact.artifactType)
      && this.normalizeCategoryKey(artifact.artifactCategory).replace(/[^a-z0-9]/g, '') === normalizedCategory);

    const sortByDefaultThenDate = (left: ArtifactAssetDto, right: ArtifactAssetDto) => {
      const leftDefault = left.default === true ? 1 : 0;
      const rightDefault = right.default === true ? 1 : 0;
      if (leftDefault !== rightDefault) {
        return rightDefault - leftDefault;
      }

      return (new Date(right.dateCreated)).getTime() - (new Date(left.dateCreated)).getTime();
    };

    const orgScoped = candidates
      .filter(artifact => artifact.organizationId === organizationId)
      .sort(sortByDefaultThenDate)[0];
    if (orgScoped) {
      return orgScoped;
    }

    const baseScoped = candidates
      .filter(artifact => artifact.artifactScope.toLowerCase() === 'base')
      .sort(sortByDefaultThenDate)[0];
    return baseScoped ?? null;
  }

  private normalizeCategoryKey(category: string): string {
    return category.toLowerCase().trim();
  }
}
